import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';

/** Jurisdiction tier → EditorialBadge tone (replaces filled level chip). */
function permitLevelTone(level: string): EditorialBadgeTone {
  const key = level.trim().toLowerCase();
  if (key === 'federal') return 'accent';
  if (key === 'state') return 'warning';
  return 'neutral';
}

export const PermittingPathwaySection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const rows = report.permittingPathway ?? [];
  const hasData = rows.length > 0;
  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <div className="space-y-2">
        {rows.map((p, i) => {
          // Critical-path rows carry a restrained DECORATIVE left accent in
          // the theme ACCENT tone (was `warning`, which surfaced the theme's
          // amber/brown status hue off-theme). Critical-path is schedule
          // emphasis, not a status warning — so it tracks the brand accent.
          // William QA 2026-06-28 — left accent rule removed report-wide
          const cardStyle: React.CSSProperties = {
            borderTop: `1px solid ${roleColor(theme, 'hair')}`,
          };
          return (
            <div key={`permit-${i}`} className="flex items-start gap-4 p-3" style={cardStyle}>
              <div className="flex flex-col items-center shrink-0">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    background: p.criticalPath
                      ? roleColorAlpha(theme, 'accent', 0.7)
                      : roleColor(theme, 'rule'),
                    boxShadow: p.criticalPath
                      ? `0 0 0 2px ${roleColorAlpha(theme, 'accent', 0.2)}`
                      : undefined,
                  }}
                />
                {i < rows.length - 1 && (
                  <div className="w-0.5 h-8 mt-1" style={{ background: roleColor(theme, 'hair') }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-bold m-0" style={{ color: roleColor(theme, 'ink') }}>{p.permit}</h4>
                  <EditorialBadge theme={theme} tone={permitLevelTone(p.level)} variant="outline">
                    {p.level}
                  </EditorialBadge>
                  {p.criticalPath && (
                    <EditorialBadge theme={theme} tone="accent" variant="outline">
                      Critical Path
                    </EditorialBadge>
                  )}
                </div>
                <div className="text-xs mt-0.5" style={{ color: roleColor(theme, 'muted') }}>
                  {p.agency} – {p.estimatedTimeline}
                </div>
                {p.dependencies && p.dependencies.length > 0 && (
                  <div className="text-xs mt-1" style={{ color: roleColor(theme, 'muted') }}>
                    Dependencies: {p.dependencies.join(', ')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
};
